import { useEffect } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as SingleSelect from '@givebutter/primitives/single-select';
import * as Tabs from '@givebutter/primitives/tabs';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateCampaign } from 'api/campaigns';
import { CustomDonationAmountForm } from 'assets/js/pages/campaign-settings/campaign-tools/CustomDonationAmountForm';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as SettingsCard from 'components/settings-card';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { capitalize } from 'lodash';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignDonationFrequency, DonationAmountValue } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface CustomDonationFormValues {
    once?: DonationAmountValue[];
    monthly?: DonationAmountValue[];
    quarterly?: DonationAmountValue[];
    yearly?: DonationAmountValue[];
    requireMinimumDonationAmount: boolean;
    showOtherAmountField: boolean;
    minimumDonationAmount?: string;
    defaultFrequency?: CampaignDonationFrequency;
    activeTab: CampaignDonationFrequency;
    enabledFrequencies: EnabledFrequencies;
    enableRecurringPlans: boolean;
}

type EnabledFrequencies = {
    [key: string]: boolean;
};

type FrequencyValues = { [K in CampaignDonationFrequency]?: DonationAmountValue[] };

const FREQUENCIES: CampaignDonationFrequency[] = ['once', 'monthly', 'quarterly', 'yearly'];

const CampaignToolsCustomDonationAmountsSettings = () => {
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();
    const { data: campaign } = useCurrentCampaign();
    const {
        id: campaignId,
        amounts,
        minimumDonationSettings,
        hideOtherAmountField,
        enableRecurringPayments,
        disableRecurringPayments,
    } = campaign!;
    const queryClient = useQueryClient();

    const computedDefaultFrequency = Object.keys(amounts).find(
        item => amounts[item as CampaignDonationFrequency].is_default,
    );

    const methods = useGivebutterForm<CustomDonationFormValues>({
        defaultValues: {
            requireMinimumDonationAmount: !!minimumDonationSettings?.active,
            showOtherAmountField: !hideOtherAmountField?.active,
            minimumDonationAmount: minimumDonationSettings?.value ?? null,
            activeTab: computedDefaultFrequency as CampaignDonationFrequency,
            defaultFrequency: computedDefaultFrequency as CampaignDonationFrequency,
            enabledFrequencies: {
                once: amounts?.once.enabled,
                monthly: amounts?.monthly.enabled,
                quarterly: amounts?.quarterly.enabled,
                yearly: amounts?.yearly.enabled,
            },
            once: amounts?.once.values,
            monthly: amounts?.monthly.values,
            quarterly: amounts?.quarterly.values,
            yearly: amounts?.yearly.values,
            enableRecurringPlans:
                (enableRecurringPayments?.active && !disableRecurringPayments?.active) ?? false,
        },
    });
    const { control, path, watch, setValue, handleSubmit } = methods;
    const [
        activeTab,
        requireMinimumDonationAmount,
        enabledFrequencies,
        defaultFrequency,
        once,
        monthly,
        quarterly,
        yearly,
    ] = watch([
        'activeTab',
        'requireMinimumDonationAmount',
        'enabledFrequencies',
        'defaultFrequency',
        'once',
        'monthly',
        'quarterly',
        'yearly',
    ]);

    const enabledFrequenciesTabs = FREQUENCIES?.filter(item => !!enabledFrequencies?.[item]);

    const { mutate: submit } = useMutation({
        mutationFn: (values: CustomDonationFormValues) => {
            const {
                showOtherAmountField,
                requireMinimumDonationAmount,
                minimumDonationAmount,
                once,
                monthly,
                quarterly,
                yearly,
                defaultFrequency,
            } = values;
            const selectedDefaultFrequency = defaultFrequency ?? enabledFrequenciesTabs?.[0];

            const settings = [
                {
                    code: Settings.HideOtherAmountField.Code,
                    active: !showOtherAmountField,
                    value: !showOtherAmountField,
                },
                {
                    code: Settings.MinimumDonationRequired.Code,
                    active: requireMinimumDonationAmount,
                    value: minimumDonationAmount ? unmaskNumber(minimumDonationAmount) : null,
                },
                {
                    exists: !!enabledFrequenciesTabs?.length,
                    code: Settings.CustomDonations.Code,
                    active: !!enabledFrequenciesTabs?.length,
                    value: {
                        once: enabledFrequenciesTabs.includes('once')
                            ? formatDonationAmountForRequest(once ?? [])
                            : amounts?.once.values,
                        monthly: enabledFrequenciesTabs.includes('monthly')
                            ? formatDonationAmountForRequest(monthly ?? [])
                            : amounts?.monthly.values,
                        quarterly: enabledFrequenciesTabs.includes('quarterly')
                            ? formatDonationAmountForRequest(quarterly ?? [])
                            : amounts?.quarterly.values,
                        yearly: enabledFrequenciesTabs.includes('yearly')
                            ? formatDonationAmountForRequest(yearly ?? [])
                            : amounts?.yearly.values,
                    },
                },
                {
                    code: Settings.EnabledPaymentFrequencies.Code,
                    active: !!enabledFrequenciesTabs?.length,
                    value: enabledFrequenciesTabs,
                },
                {
                    code: Settings.DefaultPaymentFrequency.Code,
                    active: !!enabledFrequenciesTabs?.length,
                    value: selectedDefaultFrequency ?? null,
                },
                {
                    code: Settings.DisableRecurringPayments.Code,
                    active: !values.enableRecurringPlans,
                },
            ];

            return updateCampaign({ campaignId: campaignId, data: { settings } });
        },
        onSuccess: () => {
            success(`Donation amounts updated successfully.`);
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const errorKey = key.replace(
                        'amounts.',
                        '',
                    ) as FieldPath<CustomDonationFormValues>;
                    const message = errors[key][0].replace(
                        /amounts\.[^.]+\.\d+\.(description|value)/g,
                        '$1',
                    );
                    methods.setError(errorKey, {
                        message,
                    });
                });
            } else {
                error(
                    e.response?.data?.message ??
                        'Unable to update donation amounts. Check your values and try again.',
                );
            }
        },
    });

    const formatDonationAmountForRequest = (amounts: DonationAmountValue[]) =>
        amounts.map((item: DonationAmountValue) => ({
            value: unmaskNumber(item.value ?? '0'),
            description: item?.description,
        }));

    const setActiveTab = (tab: CampaignDonationFrequency) => setValue('activeTab', tab);

    useEffect(() => {
        if (
            (!defaultFrequency && enabledFrequenciesTabs.length > 1) ||
            (defaultFrequency && !enabledFrequenciesTabs.includes(defaultFrequency))
        ) {
            setValue('defaultFrequency', enabledFrequenciesTabs?.[0]);
        }
    }, [enabledFrequenciesTabs, defaultFrequency, setValue]);

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Donation settings</SettingsCard.Heading>
                <p className={'text-sm text-gray-700'}>
                    Customize and configure the donation frequencies and suggested donation amounts
                    on your campaign.
                </p>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={'https://help.givebutter.com/en/articles/2859751-custom-donation-amounts'}
                    className={'text-sm'}
                >
                    Read the guide
                </a>
                .
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.Provider {...methods}>
                    <form
                        className="flex flex-col gap-4"
                        onSubmit={handleSubmit(submit as SubmitHandler<CustomDonationFormValues>)}
                    >
                        <div className="flex flex-col gap-y-8">
                            <div className={'flex flex-col'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <Form.Label
                                        className={'text-base'}
                                        name={path('enableRecurringPlans')}
                                    >
                                        Enable recurring plans
                                    </Form.Label>
                                    <Form.Switch
                                        control={control}
                                        name={'enableRecurringPlans'}
                                        disabled={!enableRecurringPayments?.active}
                                    />
                                </div>
                                <div className={'flex flex-col'}>
                                    <p className={'text-sm text-gray-700'}>
                                        {!enableRecurringPayments?.active ? (
                                            <p className="font-regular text-sm">
                                                <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                                                Visit your{' '}
                                                <Link
                                                    to={`/accounts/${accountId}/settings/settings`}
                                                >
                                                    Account Settings
                                                </Link>{' '}
                                                to enable recurring plans. Come back here to
                                                configure the settings for this campaign.
                                            </p>
                                        ) : (
                                            <>
                                                Turn this off if you'd like to disable recurring
                                                plans on this campaign.
                                            </>
                                        )}
                                    </p>
                                </div>
                            </div>
                            <Form.Container className="flex flex-col gap-y-2">
                                <div className={'flex flex-col'}>
                                    <p className={'text-base font-medium'}>Frequencies</p>
                                    <p className={'text-sm text-gray-700'}>
                                        Select the donation frequencies displayed on your campaign's
                                        donation form.
                                    </p>
                                </div>
                                {FREQUENCIES.map((item, index) => (
                                    <div key={item + index} className="flex flex-row gap-2">
                                        <Form.UseFormCheckboxInput
                                            name={path(`enabledFrequencies.${item}`)}
                                            onCheckedChange={(value: boolean) => {
                                                setValue(
                                                    `enabledFrequencies.${item}`,
                                                    (!value && enabledFrequenciesTabs.length < 2) ||
                                                        value,
                                                );
                                            }}
                                        />
                                        <Form.Label name={path(`enabledFrequencies.${item}`)}>
                                            {item === 'once' ? 'One-time' : capitalize(item)}
                                        </Form.Label>
                                    </div>
                                ))}
                            </Form.Container>
                            {enabledFrequenciesTabs.length > 1 && (
                                <Form.Container>
                                    <Form.Label name={path('defaultFrequency')}>
                                        Default frequency
                                    </Form.Label>
                                    <Form.SingleSelect
                                        control={control}
                                        name={'defaultFrequency'}
                                        rules={{
                                            required: 'Default frequency is required.',
                                        }}
                                    >
                                        <SingleSelect.Trigger aria-label="Default frequency" />
                                        <SingleSelect.Content>
                                            {enabledFrequenciesTabs.map(item => (
                                                <SingleSelect.Item value={item} key={item}>
                                                    <SingleSelect.ItemText>
                                                        {item === 'once'
                                                            ? 'One-time'
                                                            : capitalize(item)}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                    <Form.Error name={path('defaultFrequency')} />
                                </Form.Container>
                            )}
                            <div className="flex flex-col gap-6 border-t pt-8">
                                <div>
                                    <p className={'text-base font-medium'}>Suggested amounts</p>
                                    <p className={'text-sm text-gray-700'}>
                                        Customize suggested donation amounts and add optional
                                        descriptions.
                                    </p>
                                </div>
                                <Tabs.Root value={activeTab} variant="underline">
                                    <Tabs.List className={`mb-6 flex flex-row`}>
                                        {FREQUENCIES.map((item, index) => (
                                            <Tabs.Trigger
                                                key={'tab' + item + index}
                                                value={item}
                                                onClick={() => setActiveTab(item)}
                                            >
                                                {item === 'once' ? 'One-time' : capitalize(item)}
                                            </Tabs.Trigger>
                                        ))}
                                    </Tabs.List>
                                    {FREQUENCIES.map((item, index) => (
                                        <Tabs.Content
                                            key={'tab-content' + item + index}
                                            value={item}
                                        >
                                            <CustomDonationAmountForm
                                                type={item}
                                                disabled={!enabledFrequenciesTabs.includes(item)}
                                            />
                                        </Tabs.Content>
                                    ))}
                                </Tabs.Root>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-row gap-2">
                                    <Form.UseFormCheckboxInput
                                        name="showOtherAmountField"
                                        control={control}
                                        rules={{
                                            validate: value => {
                                                if (value) {
                                                    return true;
                                                }

                                                const frequencyValues = {
                                                    once,
                                                    monthly,
                                                    quarterly,
                                                    yearly,
                                                };

                                                const allFieldsEmpty = Object.keys(
                                                    enabledFrequencies,
                                                )
                                                    .filter(
                                                        frequency => enabledFrequencies[frequency],
                                                    )
                                                    .every(
                                                        frequency =>
                                                            !frequencyValues[
                                                                frequency as keyof FrequencyValues
                                                            ]?.length,
                                                    );

                                                return (
                                                    !allFieldsEmpty ||
                                                    'Please enable this checkbox or add a custom donation amount.'
                                                );
                                            },
                                        }}
                                    />
                                    <div className="flex flex-col gap-2">
                                        <Form.Label name={path(`showOtherAmountField`)}>
                                            Show an "Other" option to allow supporters to enter
                                            their own amount
                                        </Form.Label>
                                        <Form.Error name={path('showOtherAmountField')} />
                                    </div>
                                </div>

                                <div className="flex flex-row gap-2">
                                    <Form.UseFormCheckboxInput
                                        name={path(`requireMinimumDonationAmount`)}
                                    />
                                    <Form.Label name={path(`requireMinimumDonationAmount`)}>
                                        Require a minimum donation amount
                                    </Form.Label>
                                </div>
                                {!!requireMinimumDonationAmount && (
                                    <>
                                        <Form.Label name={path(`minimumDonationAmount`)}>
                                            Minimum amount
                                        </Form.Label>
                                        <Form.TextInputRoot
                                            control={control}
                                            name={`minimumDonationAmount`}
                                            inputRootProps={{
                                                mask: 'decimal',
                                            }}
                                        >
                                            <InputCurrencyIcon />
                                            <Form.TextInputForm placeholder={'0.00'} />
                                        </Form.TextInputRoot>
                                        <Form.UseFormError name={path(`minimumDonationAmount`)} />
                                    </>
                                )}
                            </div>
                            <div className="flex gap-4">
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </Form.Provider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { CampaignToolsCustomDonationAmountsSettings };
